// src/pages/healthcare/consultation/consultationComponents/ConsultParticularsTab.tsx
// REDESIGNED - No modals, inline editing with better UX

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, InputNumber, Input, Button, DatePicker, message, Collapse, Space, Typography, Row, Col, Tag } from "antd";
import { HeartOutlined, FileTextOutlined, AlertOutlined, CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// Types
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { CompletedAppointment } from "../../../../types/appointments/AppointmentTypes";
import { defaultParticularsData, ConsultParticularsData } from "../../../../types/appointments/ConsultTypes";

const { TextArea } = Input;
const { Panel } = Collapse;
const { Text } = Typography;

interface ConsultParticularsProps {
	consult: CompletedAppointment | null;
	patient: Patient;
	setPatient: React.Dispatch<React.SetStateAction<Patient>>;
	onDataChange: (data: string) => void;
	readOnly?: boolean;
}

const ConsultParticulars: React.FC<ConsultParticularsProps> = ({ consult, patient, setPatient, onDataChange, readOnly = false }) => {
	const [consultData, setConsultData] = useState<ConsultParticularsData>(defaultParticularsData);
	const [activeKeys, setActiveKeys] = useState<string[]>(["1"]);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevConsultRef = useRef<string | null>(null);
	const prevHeightRef = useRef<string | null>(null);
	const prevWeightRef = useRef<string | null>(null);

	// Load data
	useEffect(() => {
		const currentConsultStr = (consult?.vitals as string) || null;
		const currentHeight = patient.height || null;
		const currentWeight = patient.weight || null;

		if (currentConsultStr === prevConsultRef.current && currentHeight === prevHeightRef.current && currentWeight === prevWeightRef.current) {
			return;
		}

		prevConsultRef.current = currentConsultStr;
		prevHeightRef.current = currentHeight;
		prevWeightRef.current = currentWeight;

		let initialData = { ...defaultParticularsData };

		if (consult && consult.vitals) {
			try {
				const savedData = JSON.parse(consult.vitals as string) as Partial<ConsultParticularsData>;
				// Defensive merge (not a blind overwrite): guards against an older or partial
				// persisted blob — e.g. one saved before the `pain` field existed — so it merges
				// over defaultParticularsData instead of replacing it wholesale, avoiding an
				// undefined `vitals` crash below.
				initialData = {
					...defaultParticularsData,
					...savedData,
					vitals: { ...defaultParticularsData.vitals, ...savedData.vitals },
				};
			} catch (e) {
				console.error("Error parsing vitals data:", e);
			}
		}

		if (patient.height) {
			initialData.vitals.height_cm = parseFloat(patient.height);
		}
		if (patient.weight) {
			initialData.vitals.weight_kg = parseFloat(patient.weight);
		}

		setConsultData(initialData);
	}, [consult, patient.height, patient.weight]);

	// Calculate BMI
	useEffect(() => {
		if (consultData.vitals.height_cm && consultData.vitals.weight_kg) {
			const heightInMeters = consultData.vitals.height_cm / 100;
			const bmi = consultData.vitals.weight_kg / (heightInMeters * heightInMeters);
			const roundedBmi = parseFloat(bmi.toFixed(2));

			if (roundedBmi !== consultData.vitals.bmi) {
				setConsultData((prev) => ({
					...prev,
					vitals: {
						...prev.vitals,
						bmi: roundedBmi,
					},
				}));
			}
		}
	}, [consultData.vitals.height_cm, consultData.vitals.weight_kg]);

	const debouncedSave = useCallback(
		(data: ConsultParticularsData) => {
			if (saveTimerRef.current) {
				clearTimeout(saveTimerRef.current);
			}
			saveTimerRef.current = setTimeout(() => {
				onDataChange(JSON.stringify(data));
				setUnsavedChanges(false);
				message.success("Changes saved", 1);
				saveTimerRef.current = null;
			}, 2000);
		},
		[onDataChange]
	);

	const handleDataChange = useCallback(
		(section: keyof ConsultParticularsData, field: string, value: any) => {
			setConsultData((prev) => {
				const newData = {
					...prev,
					[section]: {
						...prev[section],
						[field]: value,
					},
				};

				if (section === "vitals" && (field === "height_cm" || field === "weight_kg")) {
					setPatient((prevPatient) => ({
						...prevPatient,
						height: field === "height_cm" ? value?.toString() : prevPatient.height,
						weight: field === "weight_kg" ? value?.toString() : prevPatient.weight,
					}));
				}

				setUnsavedChanges(true);
				debouncedSave(newData);
				return newData;
			});
		},
		[setPatient, debouncedSave]
	);

	const quickFillVitals = useCallback(() => {
		const normalVitals = {
			temperature_c: 37.0,
			heart_rate: 72,
			bp_systolic: 120,
			bp_diastolic: 80,
			respiratory_rate: 16,
			o2_saturation: 98,
			pain: 0,
		};

		setConsultData((prev) => ({
			...prev,
			vitals: { ...prev.vitals, ...normalVitals },
		}));

		setUnsavedChanges(true);
		debouncedSave({ ...consultData, vitals: { ...consultData.vitals, ...normalVitals } });
		message.success("Normal vitals applied");
	}, [consultData, debouncedSave]);

	const getBMIStatus = (bmi: number | null) => {
		if (!bmi) return null;
		if (bmi < 18.5) return { text: "Underweight", color: "blue" };
		if (bmi < 25) return { text: "Normal", color: "green" };
		if (bmi < 30) return { text: "Overweight", color: "orange" };
		return { text: "Obese", color: "red" };
	};

	const bmiStatus = getBMIStatus(consultData.vitals.bmi);

	useEffect(() => {
		return () => {
			if (saveTimerRef.current) {
				clearTimeout(saveTimerRef.current);
			}
		};
	}, []);

	return (
		<div style={{ height: "100%", overflow: "auto", position: "relative" }}>
			<Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys as string[])} ghost expandIconPosition="end" size="small">
				{/* VITALS & PHYSICAL EXAM */}
				<Panel
					header={
						<Space size={8}>
							<HeartOutlined style={{ fontSize: 14, color: "#1890ff" }} />
							<div>
								<div style={{ fontWeight: 600, fontSize: 13 }}>Vital Signs & Physical Exam</div>
								<Text type="secondary" style={{ fontSize: 10 }}>
									{consultData.vitals.heart_rate ? `HR: ${consultData.vitals.heart_rate} | ` : ""}
									{consultData.vitals.bp_systolic ? `BP: ${consultData.vitals.bp_systolic}/${consultData.vitals.bp_diastolic}` : "Enter vitals"}
								</Text>
							</div>
						</Space>
					}
					key="1"
					style={{ marginBottom: 4 }}
				>
					<Space direction="vertical" style={{ width: "100%" }} size="small">
						{/* Quick Actions */}
						{!readOnly && (
							<div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
								<Button size="small" onClick={quickFillVitals} icon={<CheckCircleOutlined />}>
									Normal Vitals
								</Button>
							</div>
						)}

						{/* Vital Signs Compact Grid */}
						<Card size="small" title={<span style={{ fontSize: 12 }}>Vital Signs</span>} style={{ background: "var(--background-color)" }}>
							<Row gutter={[8, 8]}>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Height (cm)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.height_cm} onChange={(value) => handleDataChange("vitals", "height_cm", value)} placeholder="175" disabled={readOnly} />
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Weight (kg)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.weight_kg} onChange={(value) => handleDataChange("vitals", "weight_kg", value)} placeholder="70" disabled={readOnly} />
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>BMI</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.bmi} disabled />
									{bmiStatus && (
										<Tag color={bmiStatus.color} style={{ marginTop: 2, fontSize: 9 }}>
											{bmiStatus.text}
										</Tag>
									)}
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Temp (°C)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.temperature_c} onChange={(value) => handleDataChange("vitals", "temperature_c", value)} placeholder="37.0" step={0.1} disabled={readOnly} />
								</Col>

								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>HR (bpm)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.heart_rate} onChange={(value) => handleDataChange("vitals", "heart_rate", value)} placeholder="72" disabled={readOnly} />
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>BP Sys</Text>
									<InputNumber
										size="small"
										style={{ width: "100%" }}
										value={consultData.vitals.bp_systolic}
										onChange={(value) => handleDataChange("vitals", "bp_systolic", value)}
										placeholder="120"
										disabled={readOnly}
									/>
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>BP Dia</Text>
									<InputNumber
										size="small"
										style={{ width: "100%" }}
										value={consultData.vitals.bp_diastolic}
										onChange={(value) => handleDataChange("vitals", "bp_diastolic", value)}
										placeholder="80"
										disabled={readOnly}
									/>
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Resp (/min)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.respiratory_rate} onChange={(value) => handleDataChange("vitals", "respiratory_rate", value)} placeholder="16" disabled={readOnly} />
								</Col>

								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>O₂ (%)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.o2_saturation} onChange={(value) => handleDataChange("vitals", "o2_saturation", value)} placeholder="98" disabled={readOnly} />
								</Col>
								<Col span={6}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Pain (0-10)</Text>
									<InputNumber size="small" style={{ width: "100%" }} value={consultData.vitals.pain} onChange={(value) => handleDataChange("vitals", "pain", value)} min={0} max={10} placeholder="0" disabled={readOnly} />
								</Col>
							</Row>
						</Card>

						{/* Physical Exam Inline */}
						<Card size="small" title={<span style={{ fontSize: 12 }}>Physical Examination</span>} style={{ background: "var(--background-color)" }}>
							<Row gutter={[8, 8]}>
								<Col span={24}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>General Appearance</Text>
									<TextArea
										size="small"
										rows={2}
										value={consultData.physicalExam.general}
										onChange={(e) => handleDataChange("physicalExam", "general", e.target.value)}
										placeholder="Well-appearing, no acute distress..."
										disabled={readOnly}
									/>
								</Col>
								<Col span={12}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Skin</Text>
									<TextArea size="small" rows={2} value={consultData.physicalExam.skin} onChange={(e) => handleDataChange("physicalExam", "skin", e.target.value)} placeholder="Normal color, warm, dry..." disabled={readOnly} />
								</Col>
								<Col span={12}>
									<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>HEENT</Text>
									<TextArea size="small" rows={2} value={consultData.physicalExam.heent} onChange={(e) => handleDataChange("physicalExam", "heent", e.target.value)} placeholder="Normocephalic, PERRLA..." disabled={readOnly} />
								</Col>
							</Row>
						</Card>
					</Space>
				</Panel>

				{/* PRESENT CONDITION */}
				<Panel
					header={
						<Space size={8}>
							<AlertOutlined style={{ fontSize: 14, color: "#faad14" }} />
							<div>
								<div style={{ fontWeight: 600, fontSize: 13 }}>Present Condition</div>
								<Text type="secondary" style={{ fontSize: 10 }}>
									{consultData.presentCondition.chiefComplaint || "No chief complaint entered"}
								</Text>
							</div>
						</Space>
					}
					key="2"
					style={{ marginBottom: 4 }}
				>
					<Row gutter={[8, 8]}>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Chief Complaint</Text>
							<Input size="small" value={consultData.presentCondition.chiefComplaint} onChange={(e) => handleDataChange("presentCondition", "chiefComplaint", e.target.value)} placeholder="E.g., Chest pain" disabled={readOnly} />
						</Col>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Onset Date</Text>
							<DatePicker
								size="small"
								style={{ width: "100%" }}
								value={consultData.presentCondition.onsetDate ? dayjs(consultData.presentCondition.onsetDate) : null}
								onChange={(date) => handleDataChange("presentCondition", "onsetDate", date?.toISOString())}
								disabled={readOnly}
							/>
						</Col>
						<Col span={24}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>History of Present Illness</Text>
							<TextArea
								size="small"
								rows={3}
								value={consultData.presentCondition.historyOfPresentIllness}
								onChange={(e) => handleDataChange("presentCondition", "historyOfPresentIllness", e.target.value)}
								placeholder="Detailed description of the present illness..."
								disabled={readOnly}
							/>
						</Col>
						<Col span={24}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Associated Symptoms</Text>
							<TextArea
								size="small"
								rows={2}
								value={consultData.presentCondition.symptoms}
								onChange={(e) => handleDataChange("presentCondition", "symptoms", e.target.value)}
								placeholder="List associated symptoms..."
								disabled={readOnly}
							/>
						</Col>
					</Row>
				</Panel>

				{/* ASSESSMENT & PLAN */}
				<Panel
					header={
						<Space size={8}>
							<FileTextOutlined style={{ fontSize: 14, color: "#722ed1" }} />
							<div>
								<div style={{ fontWeight: 600, fontSize: 13 }}>Assessment & Plan</div>
								<Text type="secondary" style={{ fontSize: 10 }}>
									{consultData.assessment.primaryDiagnosis || "No diagnosis entered"}
								</Text>
							</div>
						</Space>
					}
					key="4"
					style={{ marginBottom: 4 }}
				>
					<Row gutter={[8, 8]}>
						<Col span={24}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Differential Diagnosis</Text>
							<TextArea
								size="small"
								rows={2}
								value={consultData.assessment.differentialDiagnosis}
								onChange={(e) => handleDataChange("assessment", "differentialDiagnosis", e.target.value)}
								placeholder="List possible diagnoses..."
								disabled={readOnly}
							/>
						</Col>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Primary Diagnosis</Text>
							<TextArea
								size="small"
								rows={2}
								value={consultData.assessment.primaryDiagnosis}
								onChange={(e) => handleDataChange("assessment", "primaryDiagnosis", e.target.value)}
								placeholder="Primary diagnosis with ICD code..."
								disabled={readOnly}
							/>
						</Col>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Secondary Diagnosis</Text>
							<TextArea
								size="small"
								rows={2}
								value={consultData.assessment.secondaryDiagnosis}
								onChange={(e) => handleDataChange("assessment", "secondaryDiagnosis", e.target.value)}
								placeholder="Secondary diagnoses..."
								disabled={readOnly}
							/>
						</Col>
						<Col span={24}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Plan of Care</Text>
							<TextArea size="small" rows={3} value={consultData.assessment.planOfCare} onChange={(e) => handleDataChange("assessment", "planOfCare", e.target.value)} placeholder="Detailed treatment plan..." disabled={readOnly} />
						</Col>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Follow-up Plan</Text>
							<TextArea size="small" rows={2} value={consultData.assessment.followUpPlan} onChange={(e) => handleDataChange("assessment", "followUpPlan", e.target.value)} placeholder="Follow-up instructions..." disabled={readOnly} />
						</Col>
						<Col span={12}>
							<Text style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Referrals</Text>
							<TextArea size="small" rows={2} value={consultData.assessment.referrals} onChange={(e) => handleDataChange("assessment", "referrals", e.target.value)} placeholder="Specialist referrals..." disabled={readOnly} />
						</Col>
					</Row>
				</Panel>
			</Collapse>
		</div>
	);
};

export default ConsultParticulars;
